import React, { useState } from "react";
import { CalendarPlus, BookOpen, Loader2, TrendingUp, Check, ExternalLink, Users, RefreshCw, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { canUse, usePaywall } from "@/lib/access";
import { platformInfo } from "@/lib/streamers";
import { gameToIcs, downloadIcs, isExportable } from "@/lib/ics";
import { useAffiliateLinks, affiliateUrlFor } from "@/lib/affiliates";
import { trackAffiliateClick } from "@/lib/affiliateTracking";
import { useGamePreview } from "@/lib/preview";
import { useGameBetting } from "@/lib/betting";
import { useGameFantasy, FANTASY_SUPPORTED_SPORTS } from "@/lib/fantasy";
import { useSubscriptions } from "@/lib/subscriptions";
import OddsStrip from "@/components/OddsStrip";
import FavoriteButton from "@/components/FavoriteButton";

const BETTING_ACCENT = "#067647"; // sportsbook green
const FANTASY_ACCENT = "#5B2A86"; // deep purple — distinct from betting green

export default function GameRow({ game, index, displayTime, displayDate }) {
  const platforms = (game.platforms && game.platforms.length > 0)
    ? game.platforms
    : (game.platform ? [game.platform] : []);

  const affiliateLinks = useAffiliateLinks();
  const subs = useSubscriptions();
  const exportable = isExportable(game);
  const subscribedOptions = platforms.filter((p) => subs.has(platformInfo(p).label));
  const onSubscribed = subscribedOptions.length > 0;
  // "Hidden cost" nudge: user has at least one subscription BUT none match
  // the game's platforms. Highlight the first platform they'd need, with the
  // affiliate CTA so they can try it before giving up on the game.
  const needsPlatform = (subs.hasAny && platforms.length > 0 && !onSubscribed)
    ? platforms[0]
    : null;
  const needsInfo = needsPlatform ? platformInfo(needsPlatform) : null;
  const needsHref = needsInfo ? affiliateUrlFor(needsInfo.label, affiliateLinks) : null;

  const [openPreview, setOpenPreview] = useState(false);
  const [openBetting, setOpenBetting] = useState(false);
  const [openFantasy, setOpenFantasy] = useState(false);
  const { load: loadPreview, getFor: getPreview } = useGamePreview();
  const { load: loadBetting, getFor: getBetting } = useGameBetting();
  const { load: loadFantasy, getFor: getFantasy } = useGameFantasy();
  const previewState = getPreview(game);
  const bettingState = getBetting(game);
  const fantasyState = getFantasy(game);
  const fantasySupported = FANTASY_SUPPORTED_SPORTS.has(game.sport);
  const { user } = useAuth();
  const { openPaywall } = usePaywall();
  const locked = {
    game: !canUse(user, "game"),
    betting: !canUse(user, "betting"),
    fantasy: !canUse(user, "fantasy"),
  };
  const lockIcon = <Lock size={11} strokeWidth={2.8} data-testid="insight-lock-icon" />;

  const handleAddToCalendar = () => {
    const ics = gameToIcs(game);
    if (!ics) return;
    const fname = `${game.away_team}-at-${game.home_team}-${(game.date || "").slice(0, 10)}`;
    downloadIcs(fname, ics);
  };

  async function togglePreview() {
    if (locked.game) return openPaywall("game");
    if (!openPreview && !previewState.text) {
      setOpenPreview(true);
      await loadPreview(game);
    } else {
      setOpenPreview((v) => !v);
    }
  }

  async function toggleBetting() {
    if (locked.betting) return openPaywall("betting");
    if (!openBetting && !bettingState.text) {
      setOpenBetting(true);
      await loadBetting(game);
    } else {
      setOpenBetting((v) => !v);
    }
  }

  async function toggleFantasy() {
    if (locked.fantasy) return openPaywall("fantasy");
    if (!openFantasy && !fantasyState.text) {
      setOpenFantasy(true);
      await loadFantasy(game);
    } else {
      setOpenFantasy((v) => !v);
    }
  }

  async function refreshFantasy() {
    await loadFantasy(game, { force: true });
  }

  return (
    <div
      className="row-enter py-5 rule-thin"
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
      data-testid={`game-row-${index}`}
      data-on-subscribed={onSubscribed || undefined}
    >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
        {/* Time */}
        <div className="md:col-span-2 flex flex-col md:items-start md:pt-1 gap-1">
          <div
            className="font-mono text-sm md:text-base font-bold text-[#333333] uppercase leading-tight"
            data-testid={`game-time-${index}`}
          >
            {displayTime || game.time || "TIME TBD"}
          </div>
          {displayDate && (
            <div
              className="font-mono text-[10px] tracking-[0.2em] font-extrabold uppercase text-[#777]"
              data-testid={`game-date-${index}`}
            >
              {displayDate}
            </div>
          )}
          {game.live_state && game.live_state.state === "in" && (
            <div
              className="inline-flex items-center gap-1.5 font-mono text-xs font-extrabold uppercase tracking-[0.15em]"
              style={{ color: "#c0272d" }}
              data-testid={`game-live-${index}`}
            >
              <span
                className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: "#c0272d" }}
              />
              LIVE
              {game.live_state.clock && <span>· {game.live_state.clock}</span>}
              {typeof game.live_state.period === "number" && game.live_state.period > 0 && (
                <span>· Q{game.live_state.period}</span>
              )}
            </div>
          )}
          {game.live_state && game.live_state.state === "post" && game.live_state.completed && (
            <div
              className="font-mono text-xs font-extrabold uppercase tracking-[0.15em] text-[#666]"
              data-testid={`game-final-${index}`}
            >
              FINAL
            </div>
          )}
          {game.live_state && (game.live_state.state === "in" || game.live_state.state === "post") && (
            <div
              className="font-display text-sm md:text-base font-extrabold text-[#111]"
              data-testid={`game-score-${index}`}
            >
              {game.live_state.away_score ?? 0}
              <span className="mx-1.5 text-[#aaa]">–</span>
              {game.live_state.home_score ?? 0}
            </div>
          )}
        </div>

        {/* Teams */}
        <div className="md:col-span-7 flex flex-col gap-1">
          <div className="font-display text-xl md:text-2xl font-extrabold text-[#111111] leading-tight">
            <span className="inline-flex items-center gap-1.5 align-middle">
              {game.away_team}
              <FavoriteButton
                sport={game.sport}
                team={game.away_team}
                testid={`game-fav-away-${index}`}
              />
            </span>
            <span className="mx-3 inline-block opacity-50 font-mono text-base align-middle">@</span>
            <span className="inline-flex items-center gap-1.5 align-middle">
              {game.home_team}
              <FavoriteButton
                sport={game.sport}
                team={game.home_team}
                testid={`game-fav-home-${index}`}
              />
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-[0.18em] text-[#555555] font-bold">
            <span>{game.sport}</span>
            {game.source_url && (
              <>
                <span className="opacity-30">·</span>
                <a
                  href={game.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link-u text-[#111111]"
                  data-testid={`game-source-${index}`}
                >
                  source
                </a>
              </>
            )}
            {exportable && (
              <>
                <span className="opacity-30">·</span>
                <button
                  type="button"
                  onClick={handleAddToCalendar}
                  data-testid={`game-add-to-calendar-${index}`}
                  className="link-u text-[#111111] inline-flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 font-bold uppercase tracking-[0.18em] text-xs"
                  title="Add to calendar (.ics)"
                >
                  <CalendarPlus size={12} strokeWidth={2.6} />
                  add to calendar
                </button>
              </>
            )}
          </div>
        </div>

        {/* Streamer Badges */}
        <div className="md:col-span-3 flex md:justify-end items-start" data-testid={`game-platforms-${index}`}>
          {platforms.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 md:justify-end">
              {platforms.map((p, pi) => {
                const info = platformInfo(p);
                const href = affiliateUrlFor(info.label, affiliateLinks);
                const userHas = subs.has(info.label);
                const sharedProps = {
                  className: "streamer",
                  style: { background: info.color, textDecoration: "none" },
                  "data-testid": `game-platform-${index}-${pi}`,
                  title: href
                    ? `Watch on ${info.label}${userHas ? " (you subscribe)" : ""}`
                    : `${info.label}${userHas ? " (you subscribe)" : ""}`,
                };
                const inner = (
                  <>
                    {userHas
                      ? <Check size={10} strokeWidth={3.2} style={{ marginRight: 1 }} />
                      : <span className="dot" />}
                    {info.label}
                  </>
                );
                if (href) {
                  return (
                    <a
                      key={`${index}-${pi}-${info.label}`}
                      href={href}
                      target="_blank"
                      rel="sponsored noopener noreferrer"
                      onClick={() => trackAffiliateClick({
                        partner: info.label,
                        kind: "streamer",
                        context: "game-row",
                        sport: game.sport,
                        home_team: game.home_team,
                        away_team: game.away_team,
                      })}
                      {...sharedProps}
                    >
                      {inner}
                    </a>
                  );
                }
                return (
                  <span key={`${index}-${pi}-${info.label}`} {...sharedProps}>
                    {inner}
                  </span>
                );
              })}
            </div>
          ) : (
            <span className="streamer" style={{ background: "#111111" }}>
              <span className="dot" />
              CHECK LISTINGS
            </span>
          )}
        </div>
      </div>

      {/* Prominent insights CTAs — bottom left */}
      <div className="mt-4 md:ml-[16.66%] flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={togglePreview}
          data-testid={`game-preview-toggle-${index}`}
          aria-expanded={openPreview}
          title={openPreview ? "Hide game insights" : "Show game insights"}
          className="group inline-flex items-center gap-2 border-2 border-[#111] bg-[#111] text-[#f6f6f4] hover:bg-[#222] px-4 py-2 font-display font-extrabold uppercase tracking-[0.12em] text-xs md:text-sm transition-colors focus-ring active:translate-y-px"
        >
          <BookOpen size={14} strokeWidth={2.6} />
          {openPreview ? "Hide insights" : "Game insights"}
          {locked.game && lockIcon}
        </button>
        <button
          type="button"
          onClick={toggleBetting}
          data-testid={`game-betting-toggle-${index}`}
          aria-expanded={openBetting}
          title={openBetting ? "Hide betting insights" : "Show betting insights"}
          className="group inline-flex items-center gap-2 border-2 px-4 py-2 font-display font-extrabold uppercase tracking-[0.12em] text-xs md:text-sm transition-colors focus-ring active:translate-y-px"
          style={{
            borderColor: BETTING_ACCENT,
            background: openBetting ? BETTING_ACCENT : "transparent",
            color: openBetting ? "#f6f6f4" : BETTING_ACCENT,
          }}
        >
          <TrendingUp size={14} strokeWidth={2.6} />
          {openBetting ? "Hide betting" : "Betting insights"}
          {locked.betting && lockIcon}
        </button>
        {fantasySupported && (
          <button
            type="button"
            onClick={toggleFantasy}
            data-testid={`game-fantasy-toggle-${index}`}
            aria-expanded={openFantasy}
            title={openFantasy ? "Hide fantasy insights" : "Show fantasy insights"}
            className="group inline-flex items-center gap-2 border-2 px-4 py-2 font-display font-extrabold uppercase tracking-[0.12em] text-xs md:text-sm transition-colors focus-ring active:translate-y-px"
            style={{
              borderColor: FANTASY_ACCENT,
              background: openFantasy ? FANTASY_ACCENT : "transparent",
              color: openFantasy ? "#f6f6f4" : FANTASY_ACCENT,
            }}
          >
            <Users size={14} strokeWidth={2.6} />
            {openFantasy ? "Hide fantasy" : "Fantasy insights"}
            {locked.fantasy && lockIcon}
          </button>
        )}
        {/* Hidden-cost nudge — user has subs but none match this game. */}
        {needsInfo && (
          needsHref ? (
            <a
              href={needsHref}
              target="_blank"
              rel="sponsored noopener noreferrer"
              onClick={() => trackAffiliateClick({
                partner: needsInfo.label,
                kind: "streamer",
                context: "needs-nudge",
                sport: game.sport,
                home_team: game.home_team,
                away_team: game.away_team,
              })}
              data-testid={`needs-nudge-${index}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase font-extrabold border-2 transition-colors focus-ring active:translate-y-px no-underline"
              style={{ borderColor: needsInfo.color, color: needsInfo.color, background: "transparent" }}
              title={`You don't have ${needsInfo.label} — try it to watch this game`}
            >
              Needs {needsInfo.label}
              <ExternalLink size={10} strokeWidth={2.8} />
            </a>
          ) : (
            <span
              className="inline-flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold border-2"
              style={{ borderColor: needsInfo.color, color: needsInfo.color, background: "transparent" }}
              data-testid={`needs-nudge-${index}`}
            >
              Needs {needsInfo.label}
            </span>
          )
        )}
      </div>

      {/* Expanded AI game insights */}
      {openPreview && (
        <div
          className="mt-5 md:ml-[16.66%] md:mr-[25%] border-l-2 border-[#111] pl-5 pr-2"
          data-testid={`game-preview-body-${index}`}
        >
          {previewState.loading && (
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">
              <Loader2 size={12} className="spin-slow" />
              Writing insights…
            </div>
          )}
          {previewState.error && (
            <div className="font-mono text-xs uppercase tracking-[0.15em] text-[#E10A17] font-bold">
              {previewState.error}
            </div>
          )}
          {previewState.text && (
            <>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#555] font-bold mb-2">
                Game insights · AI editorial
              </div>
              <p className="font-display text-base md:text-lg leading-relaxed text-[#222] whitespace-pre-wrap">
                {previewState.text}
              </p>
            </>
          )}
        </div>
      )}

      {/* Expanded AI betting insights */}
      {openBetting && (
        <div
          className="mt-5 md:ml-[16.66%] md:mr-[25%] border-l-2 pl-5 pr-2"
          style={{ borderColor: BETTING_ACCENT }}
          data-testid={`game-betting-body-${index}`}
        >
          {bettingState.loading && (
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: BETTING_ACCENT }}>
              <Loader2 size={12} className="spin-slow" />
              Crunching betting angles…
            </div>
          )}
          {bettingState.error && (
            <div className="font-mono text-xs uppercase tracking-[0.15em] text-[#E10A17] font-bold">
              {bettingState.error}
            </div>
          )}
          {bettingState.text && (
            <>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold mb-2" style={{ color: BETTING_ACCENT }}>
                Betting insights · AI analyst
              </div>
              {bettingState.odds && <OddsStrip odds={bettingState.odds} index={index} game={game} />}
              <p className="font-display text-base md:text-lg leading-relaxed text-[#222] whitespace-pre-wrap">
                {bettingState.text}
              </p>
            </>
          )}
        </div>
      )}

      {/* Expanded AI fantasy insights */}
      {openFantasy && fantasySupported && (
        <div
          className="mt-5 md:ml-[16.66%] md:mr-[25%] border-l-2 pl-5 pr-2"
          style={{ borderColor: FANTASY_ACCENT }}
          data-testid={`game-fantasy-body-${index}`}
        >
          {fantasyState.loading && (
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase font-bold" style={{ color: FANTASY_ACCENT }}>
              <Loader2 size={12} className="spin-slow" />
              Reading props + injury wires…
            </div>
          )}
          {fantasyState.error && (
            <div className="font-mono text-xs uppercase tracking-[0.15em] text-[#E10A17] font-bold">
              {fantasyState.error}
            </div>
          )}
          {fantasyState.text && (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold" style={{ color: FANTASY_ACCENT }}>
                  Fantasy insights · AI start/sit
                  {typeof fantasyState.props_count === "number" && fantasyState.props_count > 0 && (
                    <span className="ml-2 opacity-70">· {fantasyState.props_count} prop lines read</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={refreshFantasy}
                  disabled={fantasyState.loading}
                  data-testid={`game-fantasy-refresh-${index}`}
                  title="Re-fetch — use before lineup lock"
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em] uppercase font-bold border-2 px-2 py-1 hover:opacity-80 transition-opacity focus-ring active:translate-y-px disabled:opacity-50"
                  style={{ borderColor: FANTASY_ACCENT, color: FANTASY_ACCENT }}
                >
                  <RefreshCw size={11} strokeWidth={2.6} className={fantasyState.loading ? "spin-slow" : ""} />
                  Refresh
                </button>
              </div>
              <p className="font-display text-base md:text-lg leading-relaxed text-[#222] whitespace-pre-wrap">
                {fantasyState.text}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
